import { useCallback, useEffect, useRef, useState } from 'react';
import { evidenceFileMetadata } from '@hjci-investorhub-v2/shared';
import { errorMessage } from '../../lib/api';
import { uploadEvidence } from './upload-evidence';

export interface EvidenceFile {
  key: string; file: File; status: 'queued' | 'uploading' | 'ready' | 'failed'; evidenceId?: string; error?: string;
}
export function useEvidenceFiles() {
  const [files, setFiles] = useState<EvidenceFile[]>([]);
  const [selectionError, setSelectionError] = useState('');
  const [uploading, setUploading] = useState(false);
  const current = useRef<EvidenceFile[]>([]);
  const controller = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; controller.current?.abort(); }; }, []);
  const update = useCallback((change: (previous: EvidenceFile[]) => EvidenceFile[]) => {
    current.current = change(current.current);
    if (mounted.current) setFiles(current.current);
  }, []);
  const add = (incoming: File[]) => {
    const errors: string[] = [];
    const selected: EvidenceFile[] = [];
    const keys = new Set(current.current.map(item => item.key));
    for (const file of incoming) {
      const metadata = evidenceFileMetadata(file);
      if (!metadata.success) { errors.push(`${file.name}: ${metadata.error.issues[0].message}`); continue; }
      const key = `${file.name}:${file.size}:${file.lastModified}:${metadata.data.contentType}`;
      if (keys.has(key)) continue;
      keys.add(key); selected.push({ key, file, status: 'queued' });
    }
    setSelectionError(errors.join(' '));
    update(previous => [...previous, ...selected]);
  };
  const remove = (key: string) => update(previous => previous.filter(item => item.key !== key || item.status === 'ready' || item.status === 'uploading'));
  const uploadAll = async (entityId: string): Promise<boolean> => {
    if (controller.current) return false;
    const abort = new AbortController(); controller.current = abort; setUploading(true);
    const pending = current.current.filter(item => item.status !== 'ready');
    try {
      for (const item of pending) {
        update(previous => previous.map(row => row.key === item.key ? { ...row, status: 'uploading', error: undefined } : row));
        try {
          const evidenceId = await uploadEvidence(entityId, item.file, abort.signal);
          update(previous => previous.map(row => row.key === item.key ? { ...row, status: 'ready', evidenceId } : row));
        } catch (error) {
          if (abort.signal.aborted) return false;
          update(previous => previous.map(row => row.key === item.key ? { ...row, status: 'failed', error: errorMessage(error) } : row));
        }
      }
      return current.current.every(item => item.status === 'ready');
    } finally { controller.current = null; if (mounted.current) setUploading(false); }
  };
  return { files, selectionError, uploading, add, remove, uploadAll, pending: files.filter(item => item.status !== 'ready').length };
}
